import { CommonModule, isPlatformBrowser } from "@angular/common";
import { HttpErrorResponse } from "@angular/common/http";
import { Component, Inject, InjectionToken, OnInit, PLATFORM_ID, signal } from "@angular/core";
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from "@angular/forms";
import { MAT_DIALOG_DATA, MatDialogActions, MatDialogClose, MatDialogContent, MatDialogRef } from "@angular/material/dialog";
import { CBox_PublicRolePermissionMapping, RoleExtensionsEnum } from "@server/services/cbox/public/api/v1/enforcers/entity_permission/types";
import { CBox_PublicSuccessResponse } from "@server/services/cbox/public/api/v1/resources/common/request_base/types";
import { CBox_ActionCreateUserResponse, CBox_ActionUpdateApiUserData, CBox_GetApiUserResponse, CBox_GetSelfUserResponse } from "@server/services/cbox/public/api/v1/resources/user/types";
import { AutoFocusModule } from "primeng/autofocus";
import { ButtonModule } from "primeng/button";
import { CheckboxModule } from "primeng/checkbox";
import { DividerModule } from "primeng/divider";
import { DropdownModule } from "primeng/dropdown";
import { InputGroupModule } from "primeng/inputgroup";
import { InputGroupAddonModule } from "primeng/inputgroupaddon";
import { InputTextModule } from "primeng/inputtext";
import { InputTextareaModule } from "primeng/inputtextarea";
import { MultiSelectModule } from "primeng/multiselect";
import { ProgressSpinnerModule } from "primeng/progressspinner";
import { TableModule } from "primeng/table";
import { ApiService, handlePublicApiError } from "src/services/api/api.service";
import { ConfigurationService } from "src/services/configuration/configuration.service";
import { LockersService } from "src/services/lockers/lockers.service";
import { ProfileService } from "src/services/profile/profile.service";
import { ToastService } from "src/services/toast/toast.service";
import { MappedRoleExtensionType } from "../../../types";
import { CBox_GetLockerShortData } from "@server/services/cbox/public/api/v1/resources/locker/types";

@Component({
  selector: "app-cbox-profile-user-create",
  templateUrl: "cbox-profile-api-user-edit.component.html",
  styleUrls: ["./cbox-profile-api-user-edit.component.scss"],
  standalone: true,
  imports: [
    CommonModule,
    TableModule,
    ReactiveFormsModule,
    InputTextModule,
    ButtonModule,
    DividerModule,
    InputGroupModule,
    InputGroupAddonModule,
    MatDialogContent,
    MatDialogActions,
    MatDialogClose,
    AutoFocusModule,
    DropdownModule,
    MultiSelectModule,
    CheckboxModule,
    ProgressSpinnerModule,
    InputTextareaModule
  ]
})

export class CBoxProfileApiUserEditComponent implements OnInit {

  apiUserCreateStatus = signal<"waiting" | "created" | "creating">("waiting");
  apiUserCreateResponse = signal<CBox_ActionCreateUserResponse | undefined>(undefined);
  userForm: FormGroup;
  userData = signal<CBox_GetSelfUserResponse | undefined>(undefined);
  zones = signal<Record<number, { id: string; name: string }>[]>([]);
  zonesFetched = signal(false);
  roles = signal<CBox_PublicRolePermissionMapping | undefined>(undefined);

  roleExtensions = signal<MappedRoleExtensionType[]|undefined>(undefined);
  roleExtensionsMapping = signal<Record<RoleExtensionsEnum, string> | undefined>(undefined);
  oldRoleExtensions = signal<RoleExtensionsEnum[]>([]);
  showOldExtensions = signal(false);

  get lockersForm(): FormGroup {
    return this.userForm.get("lockers") as FormGroup;
  }

  constructor(
    private profileService: ProfileService,
    private api: ApiService,
    private fb: FormBuilder,
    private lockerService: LockersService,
    private config: ConfigurationService,
    private toastService: ToastService,
    private dialogRef: MatDialogRef<CBoxProfileApiUserEditComponent>,
    @Inject(MAT_DIALOG_DATA) private identifier: string,
    @Inject(PLATFORM_ID) private platformId: InjectionToken<Object>) {
      this.userForm = this.fb.group({
        description: [""],
        role: ["", Validators.required],
        roleExtensions: [[]],
        zoneId: [undefined, Validators.required]
      })
    }

  ngOnInit(): void {
    this.init();
  }

  loadData(): void {
    if (isPlatformBrowser(this.platformId)) {
      this.fetchZones();
      this.fetchUserData();
    }
  }

  saveUser(): void {
    this.apiUserCreateStatus.set("creating");
    this.api.patch("backend/users/api/" + this.identifier, this.getUpdateStructure()).subscribe(() => {
      this.dialogRef.close(true);
      this.apiUserCreateStatus.set("created");
    }, (e: HttpErrorResponse) => {
      this.apiUserCreateStatus.set("waiting");
      handlePublicApiError(e, this.toastService);
    });
  }

  mapRoleExtensions(roleExtensions: RoleExtensionsEnum[]): string {
    return roleExtensions.map((extension) => {
      return this.config.mapExtendedPermissions(extension).toLowerCase();
    }).join(", ");
  }

  private fetchZones(): void {
    this.api.get<CBox_PublicSuccessResponse<(CBox_GetLockerShortData & {
      zones: {
        id: string;
        name: string;
      }[]
    })[]>>("backend/locker/identifiers?includeZones=true").subscribe((response) => {
      const zones: {id: string; name: string}[] = [];
      for (const locker of response.data) {
        for (const zone of locker.zones) {
          if (!zones.find(z => z.id === zone.id)) {
            zones.push({id: zone.id, name: zone.name});
          }
        }
      }
      this.zones.set(zones);
      this.zonesFetched.set(true);
    }, (e: HttpErrorResponse) => {
      handlePublicApiError(e, this.toastService);
    });
  }

  async roleExtensionsChanged(event: any): Promise<void> {
    const selectedOption = event?.itemValue;
    if (!selectedOption) {
      return;
    }

    const extensions = await this.config.getRoleExtensions();
    const selectedGrant = extensions?.canGrant?.find(
      (grant) => grant.name === selectedOption.value
    );
    const selectedGroup = selectedGrant?.group;

    this.roleExtensions.update((current) => {
      const wasSelected = !!event?.originalEvent?.selected;

      const updated = current?.map((roleExtension) => {
        const roleGrant = extensions?.canGrant?.find(
          (grant) => grant.name === roleExtension.value
        );
        const roleGroup = roleGrant?.group;

        let shouldDisable = false;

        if (wasSelected) {
          // Disable other items in the same group when an item is selected
          shouldDisable =
            (selectedGroup !== undefined &&
            roleExtension.value !== selectedOption.value &&
            roleGroup === selectedGroup) ||
            roleExtension.disabled;
        } else {
          // When an item is deselected, re-enable items in the same group
          if (roleGroup === selectedGroup) {
            shouldDisable = false;
          } else {
            // Preserve existing disabled state for other groups
            shouldDisable = roleExtension.disabled;
          }
        }

        return {
          ...roleExtension,
          disabled: shouldDisable,
        };
      });

      return updated;
    });
  }

  private async init(): Promise<void> {
    this.profileService.setTitle("Detalii cont");
    const roles = await this.config.getRolePermissionMapping()
    await this.setRoleExtensions();
    this.roles.set(this.filterRoles(roles));
    this.loadData();
  }

  private async setRoleExtensions(): Promise<void> {
    this.roleExtensionsMapping.set(this.config.roleExtensionsMapping());
    const extensions = await this.config.getRoleExtensions();
    const roleExtensions: MappedRoleExtensionType[] = [];
    for (const extension of extensions.canGrant) {
      roleExtensions.push({
        value: extension.name,
        label: this.config.mapExtendedPermissions(extension.name),
        disabled: false
      });
    }

    this.roleExtensions.set(roleExtensions);
  }

  private async updateRoleExtensionsDisabledState(): Promise<void> {
    const selectedValues = this.userForm.get('roleExtensions')?.value || [];
    const extensions = await this.config.getRoleExtensions();

    const selectedGroups = new Set(
      extensions.canGrant
        .filter((ext) => selectedValues.includes(ext.name))
        .map((ext) => ext.group)
    );

    this.roleExtensions.update((current) => {
      return current?.map((roleExtension) => {
        const roleGrant = extensions.canGrant.find((grant) => grant.name === roleExtension.value);
        const roleGroup = roleGrant?.group;

        const isSelected = selectedValues.includes(roleExtension.value);
        const isGroupSelected = selectedGroups.has(roleGroup || "");

        return {
          ...roleExtension,
          disabled: isGroupSelected && !isSelected,
        };
      });
    });
  }

  private fetchUserData(): void {
    this.api.get<CBox_PublicSuccessResponse<CBox_GetApiUserResponse>>("backend/users/api/" + this.identifier).subscribe((response) => {
      const data = response.data;
      this.userForm.patchValue({
        description: data.description,
        role: data.role,
        roleExtensions: data.roleExtensions,
        zoneId: data.zoneId
      });
      this.updateRoleExtensionsDisabledState();
    });
  }

  private filterRoles(roles: CBox_PublicRolePermissionMapping): CBox_PublicRolePermissionMapping {
    return roles.filter((role) => role.canCreate);
  }

  private getUpdateStructure(): CBox_ActionUpdateApiUserData["body"] {
    const data: CBox_ActionUpdateApiUserData["body"] = {
      role: this.userForm.get("role")?.value,
      description: this.userForm.get("description")?.value,
      zoneId: this.userForm.get("zoneId")?.value,
      roleExtensions: this.userForm.get("roleExtensions")?.value ?? [],
    };

    return data;
  }
}